import { ComponentType, ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom'

import { useBreakPoints } from 'common/hooks'

import NotFound from 'errorPages/NotFound'

import { Button, Card, Col, Menu, MenuItem, Row } from '..'
import Layout from '../layout/Layout'
import Sider from '../layout/Sider'

import './SideNavWrapper.less'

export interface MenuItem {
  name: string
  key: number
  path: string
  componentTitle?: string
  component: ComponentType<any>
  componentProps?: Record<string, any> | null
}

interface Props {
  title: string
  rootPath: string
  navStartingPositon: number
  navigationPathMapping: Map<string, number>
  navigationMenu: MenuItem[]
  children?: ReactNode
}

const SideNavWrapper = ({
  title,
  rootPath,
  navStartingPositon,
  navigationPathMapping,
  navigationMenu,
  children,
}: Props) => {
  const { t: translate } = useTranslation()
  const { isXs: isMobileView, isLg: isDesktopView, isXl } = useBreakPoints()

  const history = useHistory()
  const location = useLocation()
  const match = useRouteMatch()

  const [activeKey, setActiveKey] = useState<string[] | undefined>()

  useEffect(() => {
    let pathname = location.pathname
    const split = pathname.split('/')[3]
    const res = navigationPathMapping.get(`/${split}`)
    setActiveKey(res ? [res.toString()] : [navStartingPositon.toString()])
  }, [location])

  const goToPath = (path: string) => {
    history.push(`${rootPath}${path}`)
  }

  const menuItems = navigationMenu
    .sort((first, second) => first.key - second.key)
    .map(({ key, name, path }: MenuItem) => (
      <MenuItem key={key} onClick={() => goToPath(path)}>
        {translate(name)}
      </MenuItem>
    ))

  const navButton = navigationMenu
    .sort((first, second) => first.key - second.key)
    .map(({ key, path, name }: MenuItem) => (
      <Button
        key={key}
        onClick={() => goToPath(path)}
        className={`${activeKey?.find((i) => i === key.toString()) ? 'active' : ''}`}
        type="link"
      >
        {translate(name)}
      </Button>
    ))

  return (
    <Card title={translate(`admin-dashboard.${title}.title`)} className="side-wrapper">
      <Layout className="content-body">
        {isDesktopView && (
          <Sider width={isXl ? 240 : 160}>
            <Menu selectedKeys={activeKey} mode="inline" className="side-nav">
              {menuItems}
            </Menu>
          </Sider>
        )}
        {isMobileView && <div className="side-mobile-wrap mb-16">{navButton}</div>}
        <Layout className="side-layout-content">
          <Switch>
            {navigationMenu
              .sort((first, second) => second.key - first.key)
              .map(({ key, path, componentTitle, component: Component, componentProps }: MenuItem) => (
                <Route
                  key={key}
                  path={match.path + path}
                  render={(props) => (
                    <>
                      {componentTitle && (
                        <Row>
                          <Col md={24} className="mb-16 text-semi-bold">
                            {translate(componentTitle)}
                          </Col>
                        </Row>
                      )}
                      <Component {...props} {...componentProps} />
                    </>
                  )}
                />
              ))}
            <Route>
              <NotFound />
            </Route>
          </Switch>
          {children}
        </Layout>
      </Layout>
    </Card>
  )
}

export default SideNavWrapper
